import { useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAuth } from "../lib/auth-context.js";
import {
  deleteCollegePlan,
  listCollegePlans,
} from "../lib/api-modules/college-plans.js";
import { listFamilyMembers } from "../lib/api-modules/family-members.js";
import type {
  CollegePlan,
  FamilyMember,
  MembershipRole,
} from "../lib/api-modules/types.js";
import { formatCentsCompact, formatCentsCurrency } from "../lib/util/money.js";
import { projectCollegePlan } from "../lib/util/college-projector.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { NotesPanel } from "../components/notes-panel.js";
import { CollegePlanForm } from "../components/college-plans/CollegePlanForm.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

/**
 * /college-plans — Phase 5 Session 5. Daughter-focused (and beyond)
 * college funding projector. Saved scenarios persist via tightened
 * `college_plans` schema; the pure projector helper drives the chart on
 * each render so adjustments to inflation / growth assumptions are
 * instantaneous.
 */
export function CollegePlansPage() {
  const { user } = useAuth();
  if (!user) return null;
  const canWrite = !READ_ONLY_ROLES.has(user.role);
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: plansData, isPending, isError, error } = useQuery({
    queryKey: ["college-plans"],
    queryFn: () => listCollegePlans(false),
  });
  const { data: familyData } = useQuery({
    queryKey: ["family-members", { includeArchived: true }],
    queryFn: () => listFamilyMembers(true),
  });
  const members = familyData?.family_members ?? [];

  return (
    <div className="mx-auto max-w-6xl p-6">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">College plans</h1>
          <p className="text-sm text-zinc-500">
            Project funding trajectories per beneficiary. Pure-helper
            chart updates as you tweak assumptions.
          </p>
        </div>
      </header>

      {!canWrite && (
        <p className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          Read-only role — viewing existing plans without create/edit
          controls.
        </p>
      )}

      {canWrite && !creating && (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="mb-4 rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
        >
          + New college plan
        </button>
      )}

      {creating && (
        <div className="mb-6">
          <CollegePlanForm
            mode="create"
            members={members}
            onClose={() => setCreating(false)}
          />
        </div>
      )}

      {isPending && <p className="text-sm text-zinc-500">Loading plans…</p>}
      {isError && (
        <ErrorMessage>
          Couldn't load plans:{" "}
          {error instanceof Error ? error.message : "unknown"}
        </ErrorMessage>
      )}

      {plansData && plansData.college_plans.length === 0 && !creating && (
        <p className="text-sm text-zinc-500">
          No college plans yet. {canWrite ? "Click + New college plan to start." : ""}
        </p>
      )}

      {plansData && plansData.college_plans.length > 0 && (
        <ul className="space-y-3">
          {plansData.college_plans.map((plan) => (
            <CollegePlanCard
              key={plan.plan_id}
              plan={plan}
              members={members}
              expanded={expandedId === plan.plan_id}
              onToggle={() =>
                setExpandedId((cur) =>
                  cur === plan.plan_id ? null : plan.plan_id,
                )
              }
              canWrite={canWrite}
            />
          ))}
        </ul>
      )}
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.collegePlans} />
      </div>
    </div>
  );
}

function CollegePlanCard({
  plan,
  members,
  expanded,
  onToggle,
  canWrite,
}: {
  readonly plan: CollegePlan;
  readonly members: FamilyMember[];
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const beneficiary = members.find(
    (m) => m.member_id === plan.beneficiary_member_id,
  );

  const archive = useMutation({
    mutationFn: () => deleteCollegePlan(plan.plan_id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["college-plans"] });
    },
  });

  const projection = useMemo(() => {
    const startYear = new Date().getUTCFullYear();
    return projectCollegePlan({
      start_balance_cents: 0,
      monthly_contribution_cents: plan.monthly_contribution_cents ?? 0,
      growth_rate_pct: plan.growth_rate_pct,
      start_year: startYear,
      college_start_year: Math.max(plan.target_college_start_year, startYear),
      college_years: plan.college_years,
      cost_today_cents: plan.target_cost_today_cents,
      cost_inflation_pct: plan.cost_inflation_pct,
    });
  }, [plan]);

  return (
    <li className="rounded-md border border-zinc-200 dark:border-zinc-800">
      <div className="flex flex-wrap items-baseline justify-between gap-2 p-4">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="text-left"
        >
          <h2 className="text-lg font-semibold">{plan.name}</h2>
          <p className="text-xs text-zinc-500">
            {beneficiary?.name ?? "(beneficiary)"} · College starts{" "}
            {plan.target_college_start_year} · {plan.college_years} yrs ·{" "}
            {formatCentsCurrency(plan.target_cost_today_cents)}/yr today's-$
          </p>
        </button>
        <div className="flex items-center gap-2 text-sm">
          <Stat
            label="Total needed"
            value={formatCentsCompact(projection.total_needed_cents)}
          />
          <Stat
            label="Surplus / (shortfall)"
            value={formatCentsCompact(projection.surplus_or_shortfall_cents)}
            tone={
              projection.surplus_or_shortfall_cents >= 0 ? "positive" : "warning"
            }
          />
          {canWrite && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(true);
                }}
                className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
              >
                Edit
              </button>
              <ConfirmButton
                label="Archive"
                confirmLabel="Confirm archive"
                onConfirm={() => archive.mutate()}
                isPending={archive.isPending}
                size="xs"
              />
            </>
          )}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-zinc-200 p-4 dark:border-zinc-800">
          {editing ? (
            <CollegePlanForm
              mode="edit"
              plan={plan}
              members={members}
              onClose={() => setEditing(false)}
            />
          ) : (
            <>
              <ProjectorChart projection={projection} />
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat
                  label="Monthly contribution"
                  value={formatCentsCurrency(
                    plan.monthly_contribution_cents ?? 0,
                  )}
                />
                <Stat
                  label="Growth rate"
                  value={`${plan.growth_rate_pct}%`}
                />
                <Stat
                  label="Cost inflation"
                  value={`${plan.cost_inflation_pct}%`}
                />
                <Stat
                  label="Monthly shortfall"
                  value={
                    projection.monthly_shortfall_cents > 0
                      ? formatCentsCurrency(projection.monthly_shortfall_cents)
                      : "—"
                  }
                  tone={
                    projection.monthly_shortfall_cents > 0
                      ? "warning"
                      : "muted"
                  }
                />
              </div>
              <NotesPanel
                entityType="college_plan"
                entityId={plan.plan_id}
                canWriteOverride={canWrite}
              />
            </>
          )}
        </div>
      )}
    </li>
  );
}

function ProjectorChart({
  projection,
}: {
  readonly projection: ReturnType<typeof projectCollegePlan>;
}) {
  const data = projection.schedule.map((row) => ({
    year: row.year,
    balance: Math.max(0, row.balance_cents) / 100,
    cost: row.projected_annual_cost_cents / 100,
  }));
  return (
    <div className="h-72 w-full">
      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
          <Tooltip
            formatter={(v: unknown) =>
              typeof v === "number" ? `$${v.toLocaleString()}` : String(v)
            }
          />
          <Area
            type="monotone"
            dataKey="balance"
            stroke="#2563eb"
            fill="#3b82f6"
            fillOpacity={0.25}
            name="Plan balance"
          />
          <Area
            type="monotone"
            dataKey="cost"
            stroke="#f97316"
            fill="#fdba74"
            fillOpacity={0.2}
            name="Projected annual cost"
          />
          <ReferenceLine y={0} stroke="#a3a3a3" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  readonly label: string;
  readonly value: string;
  readonly tone?: "neutral" | "positive" | "warning" | "muted";
}) {
  const toneClass =
    tone === "positive"
      ? "text-emerald-700 dark:text-emerald-300"
      : tone === "warning"
        ? "text-amber-700 dark:text-amber-300"
        : tone === "muted"
          ? "text-zinc-500"
          : "";
  return (
    <div className="rounded-md border border-zinc-200 px-3 py-2 dark:border-zinc-800">
      <p className="text-xs text-zinc-500">{label}</p>
      <p className={`text-sm font-medium tabular-nums ${toneClass}`}>{value}</p>
    </div>
  );
}
